import React, { useState } from 'react';
import './MacdEntryConditionsBar.css';

/**
 * Entry condition status for MACD strategy
 */
export interface MacdEntryCondition {
  is_histogram_recovering: boolean;
  is_macd_below_signal: boolean;
  is_rsi_moderately_oversold: boolean;
  is_regime_favorable: boolean;
  is_volatility_acceptable: boolean;
  is_above_pivot_support: boolean;
  all_conditions_met: boolean;
  conditions_met_count: number;
}

/**
 * Props for MacdEntryConditionsBar component
 */
export interface MacdEntryConditionsBarProps {
  entryConditions?: MacdEntryCondition;
  isLoading?: boolean;
  isError?: boolean;
  className?: string;
}

/**
 * Configuration for each condition section
 */
const CONDITION_CONFIG = [
  {
    key: 'is_histogram_recovering' as keyof MacdEntryCondition,
    label: 'Histogram Recovering',
    shortLabel: 'HIST',
  },
  {
    key: 'is_macd_below_signal' as keyof MacdEntryCondition,
    label: 'MACD Below Signal',
    shortLabel: 'MACD',
  },
  {
    key: 'is_rsi_moderately_oversold' as keyof MacdEntryCondition,
    label: 'RSI Oversold (<40)',
    shortLabel: 'RSI',
  },
  {
    key: 'is_regime_favorable' as keyof MacdEntryCondition,
    label: 'Regime Favorable',
    shortLabel: 'MKT',
  },
  {
    key: 'is_volatility_acceptable' as keyof MacdEntryCondition,
    label: 'Volatility Acceptable',
    shortLabel: 'VOL',
  },
  {
    key: 'is_above_pivot_support' as keyof MacdEntryCondition,
    label: 'Above S2 Pivot',
    shortLabel: 'PIV',
  },
];

/**
 * MacdEntryConditionsBar Component
 *
 * Displays a visual indicator of entry condition status for MACD Reversion trade groups.
 * Shows 6 sections (HIST, MACD, RSI, MKT, VOL, PIV) with color-coded status.
 */
const MacdEntryConditionsBar: React.FC<MacdEntryConditionsBarProps> = ({
  entryConditions,
  isLoading = false,
  isError = false,
  className = '',
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const conditionsMetCount = entryConditions?.conditions_met_count ?? 0;

  if (isError) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={`macd-entry-conditions-bar ${className}`}>
        <div className="entry-conditions-sections loading">
          {CONDITION_CONFIG.map((_, index) => (
            <div key={index} className="entry-condition-section skeleton">
              <div className="skeleton-shimmer"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!entryConditions) {
    return null;
  }

  return (
    <div className={`macd-entry-conditions-bar ${className}`}>
      <div className="entry-conditions-summary">
        {conditionsMetCount}/6 conditions met
      </div>

      <div className="entry-conditions-sections">
        {CONDITION_CONFIG.map((config, index) => {
          const conditionValue = entryConditions[config.key];
          const isMet = conditionValue === true;
          const isUnmet = conditionValue === false;
          const isUnknown = conditionValue === null || conditionValue === undefined;

          let sectionClass = 'entry-condition-section';
          if (isMet) sectionClass += ' met';
          else if (isUnmet) sectionClass += ' unmet';
          else if (isUnknown) sectionClass += ' unknown';

          let symbol = '?';
          if (isMet) symbol = '\u2713';
          else if (isUnmet) symbol = '\u2717';

          return (
            <div
              key={config.key}
              className={sectionClass}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              role="status"
              aria-label={`${config.label}: ${isMet ? 'met' : isUnmet ? 'not met' : 'unknown'}`}
              tabIndex={0}
            >
              <span className="condition-symbol">{symbol}</span>

              {hoveredIndex === index && (
                <div className="entry-condition-tooltip" role="tooltip">
                  {config.label}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MacdEntryConditionsBar;
